import React, { useEffect, useRef, useState, useCallback } from "react";
import { Paperclip, Trash2, Upload, FileText, Image as ImageIcon, FileBox } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";

import { filesApi } from "@/lib/api";
import { confirm } from "@/lib/dialog";

const KIND_ICON = { photo: ImageIcon, plan: FileBox, doc: FileText, other: Paperclip };
const KIND_LABEL = { photo: "PHOTO", plan: "PLAN", doc: "DOC", other: "FILE" };

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function FilesSection({ taskId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!taskId) return;
    try {
      const list = await filesApi.listForTask(taskId);
      setFiles(list);
    } catch (e) {
      console.warn("Failed to list files", e);
    }
  }, [taskId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upload = useCallback(async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      for (const f of fileList) {
        try {
          await filesApi.upload(taskId, f);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Failed to upload ${f.name}`);
        }
      }
      await refresh();
      toast.success(`Uploaded ${fileList.length} file${fileList.length === 1 ? "" : "s"}.`);
    } finally {
      setUploading(false);
    }
  }, [taskId, refresh]);

  const remove = async (f) => {
    const ok = await confirm({
      title: `Delete "${f.filename}"?`,
      body: "This file will be removed from the task.",
      confirmLabel: "DELETE",
      danger: true,
    });
    if (!ok) return;
    try {
      await filesApi.remove(f.id);
      setFiles((arr) => arr.filter((x) => x.id !== f.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
    }
  };

  const onPick = (e) => upload(Array.from(e.target.files || []));
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(Array.from(e.dataTransfer?.files || []));
  };

  return (
    <div className="space-y-2" data-testid="files-section">
      {files.map((f) => {
        const Icon = KIND_ICON[f.kind] || Paperclip;
        const isImage = f.mime_type?.startsWith("image/");
        return (
          <div
            key={f.id}
            className="flex items-start gap-2 p-2 border border-line bg-bg-elevated"
            data-testid={`file-row-${f.id}`}
          >
            <Icon className="w-3.5 h-3.5 text-amber mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <a
                href={filesApi.downloadUrl(f.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-ink hover:text-amber truncate block"
                title={f.filename}
              >
                {f.filename}
              </a>
              <div className="mono text-2xs text-ink-mute uppercase tracking-wider mt-0.5">
                {KIND_LABEL[f.kind] || "FILE"} · {fmtSize(f.size_bytes)}
              </div>
              {isImage && (
                <img
                  src={filesApi.downloadUrl(f.id)}
                  alt={f.filename}
                  className="mt-1.5 max-h-32 max-w-full border border-line object-contain bg-bg-base"
                  loading="lazy"
                />
              )}
            </div>
            <button
              onClick={() => remove(f)}
              className="text-ink-mute hover:text-red shrink-0"
              title="Delete file"
              data-testid={`file-delete-${f.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        );
      })}

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={clsx(
          "flex items-center justify-center gap-2 p-3 border border-dashed cursor-pointer transition-colors",
          dragOver ? "border-amber bg-amber-glow text-amber" : "border-line text-ink-mute hover:border-amber hover:text-amber",
          uploading && "opacity-60 pointer-events-none"
        )}
        data-testid="file-upload-dropzone"
      >
        <Upload className="w-3 h-3" />
        <span className="mono text-2xs uppercase tracking-widest">
          {uploading ? "Uploading…" : "Drop files or click to upload"}
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={onPick}
          className="hidden"
          data-testid="file-upload-input"
        />
      </div>

      {!files.length && !uploading && (
        <div className="mono text-2xs text-ink-mute uppercase tracking-widest py-1">
          — no attachments —
        </div>
      )}
    </div>
  );
}
